import * as yup from 'yup';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { CheckoutFlow } from 'src/payments/constants';
import {
  ONBOARDING_MODAL_NAMES,
  ONBOARDING_MODAL_ACTIONS,
  ONBOARDING_MODAL_SOURCES,
  WALLET_PASS_SURFACES,
  LOYALTY_ENROLLMENT_SURFACES,
  LOYALTY_ENROLLMENT_ACTIONS,
  LOYALTY_ENROLLMENT_INTEGRATIONS,
  PHONE_VERIFICATION_NUDGE_SURFACES,
  PHONE_VERIFICATION_NUDGE_ACTIONS,
  PHONE_VERIFICATION_NUDGE_LEGS,
  ACCOUNT_SIGNUP_ACTIONS,
  ACCOUNT_SIGNUP_METHODS,
  ACCOUNT_SIGNUP_SURFACES,
  STORE_ROUTING_ACTIONS,
  STORE_ROUTING_FAILURE_REASONS,
  STORE_ROUTING_SOURCES,
} from 'src/utils/analytics/events';

// prettier-ignore
const baseEventSchema = yup.object({
  dispensary_id: yup
    .string()
    .required(),
  menu_variant: yup
    .string()
    .oneOf(Object.values(InterfaceVariants))
    .required(),
  page: yup
    .string()
    .optional(),
});

// prettier-ignore
const itemSchema = yup.object({
  id: yup
    .string()
    .required(),
  library_product_id: yup
    .string()
    .nullable(),
  library_product_score: yup
    .number()
    .nullable(),
  brand_id: yup
    .string()
    .nullable(),
  parent_brand_id: yup
    .string()
    .nullable(),
})

// prettier-ignore
export const cartItemSchema = itemSchema.shape({
  price: yup
    .number()
    .required(),
  quantity: yup
    .number()
    .required(),
  option: yup
    .string()
    .nullable(),
  tracker_source: yup
    .string()
    .nullable()
    .optional(),
  position: yup
    .number()
    .optional(),
});

// prettier-ignore
export const placedOrderCartItemSchema = cartItemSchema.shape({
  base_price: yup
    .number()
    .required(),
  discounted_base_price: yup
    .number()
    .nullable(),
  total_price: yup
    .number()
    .required(),
});

const addedProductToCartSchema = baseEventSchema.shape({
  item: cartItemSchema,
});

const removedProductFromCartSchema = baseEventSchema.shape({
  item: cartItemSchema,
});

// prettier-ignore
const placedOrderSchema = baseEventSchema.shape({
  subtotal: yup
    .number()
    .required(),
  total: yup
    .number()
    .required(),
  discount: yup
    .number()
    .required(),
  final_total: yup
    .number()
    .required(),
  items: yup
    .array()
    .of(placedOrderCartItemSchema)
    .required(),
  order_id: yup
    .string()
    .required(),
  payment_method: yup
    .string()
    .required(),
  round_up_the_change_eligible: yup
    .boolean()
    .nullable(),
  round_up_the_change_fee_donation_id: yup
    .number()
    .nullable(),
  round_up_the_change_selected: yup
    .boolean()
    .nullable(),
  round_up_the_change_amount: yup
    .string()
    .nullable(),
});

// prettier-ignore
const searchResultsSchema = baseEventSchema.shape({
  query: yup
    .string()
    .required(),
});

// prettier-ignore
const viewedPageSchema = baseEventSchema.shape({
  pageType: yup
    .string(),
});

// prettier-ignore
const viewedProductSchema = baseEventSchema.shape({
  item: itemSchema,
});

const cartViewedSchema = baseEventSchema;

const dutchiePayCTAButtonClickedSchema = baseEventSchema;

const dutchiePayRelinkButtonViewedSchema = baseEventSchema;
const dutchiePayRelinkButtonClickedSchema = baseEventSchema;
const dutchiePayUnlinkRemoveCtaClickedSchema = baseEventSchema;
const dutchiePayUnlinkModalViewedSchema = baseEventSchema;
const dutchiePayUnlinkConfirmedSchema = baseEventSchema;
const dutchiePayUnlinkCancelledSchema = baseEventSchema;
const dutchiePayUnlinkSuccessSchema = baseEventSchema;
const dutchiePayUnlinkErrorSchema = baseEventSchema;

// prettier-ignore
const productClickedSchema = baseEventSchema.shape({
  item: itemSchema,
  placement: yup.
    string()
    .nullable(),
  position: yup.
    number()
    .nullable(),
  provider: yup.
    string()
    .nullable(),
});

// prettier-ignore
const trackerItemSchema = yup.object({
  id: yup
    .string()
    .nullable(),
  library_product_id: yup
    .string()
    .nullable(),
  brand_id: yup
    .string()
    .nullable(),
  parent_brand_id: yup
    .string()
    .nullable(),
})

// prettier-ignore
const productTrackerSchema = baseEventSchema.shape({
  tracking_url: yup
    .string()
    .required(),
  position: yup
    .number()
    .nullable(),
  item: trackerItemSchema,
})

// prettier-ignore
const bannerTrackerSchema = baseEventSchema.shape({
  tracking_url: yup
    .string()
    .required(),
  items: yup
    .array()
    .of(trackerItemSchema),
})

const dispensaryClickedSchema = baseEventSchema.shape({
  dispensary_name: yup.string().required(),
  dispensary_address: yup.string().required(),
  dispensary_chain_name: yup.string().required(),
  source: yup.string().required(),
  meta: yup.object().shape({
    product: yup.object().required(),
  }),
});

const baseDeviceTrackingFields = {
  is_kiosk_order: yup.boolean().required(),
  is_hub_order: yup.boolean().required(),
  checkout_token: yup.string().nullable(),
};

const dutchiePayEnrollmentStepSchema = baseEventSchema.shape({
  step: yup.string().required(),
  action: yup.string().required(),
  enrollment_source: yup.string().optional(),
  context: yup.string().optional(),
  cta_placement: yup.string().optional(),
  pbb_visitor_id: yup.string().optional(),
  ...baseDeviceTrackingFields,
});

const dutchiePayEnrollmentButtonClickedSchema = baseEventSchema.shape({
  button_location: yup.string().required(),
});

const dutchiePayEnrollmentStepCancelledSchema = baseEventSchema.shape({
  step: yup.string().required(),
  ...baseDeviceTrackingFields,
});

const dutchiePayPlaidOnEventSchema = baseEventSchema.shape({
  event_name: yup.string().required(),
  connection_method: yup.string().required(),
  error_code: yup.string().nullable(),
  error_message: yup.string().nullable(),
  institution_id: yup.string().nullable(),
  institution_name: yup.string().nullable(),
  mfa_type: yup.string().nullable(),
  selection: yup.string().nullable(),
  view_name: yup.string().nullable(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayPlaidOnLoadSchema = baseEventSchema.shape({
  connection_method: yup.string().required(),
  dutchie_pay_session_token: yup.string().nullable(),
  phone_passed_to_plaid: yup.boolean().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayIncentiveExposureSchema = baseEventSchema.shape({
  user_type: yup.string().required(),
  incentive_value: yup.string().nullable(),
  is_cash_back: yup.boolean().nullable(),
  source: yup.string().nullable(),
  reward_type: yup.string().oneOf(['cash_back', 'points_boost']).optional(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayServiceFeeShownSchema = baseEventSchema.shape({
  fees_disabled: yup.boolean().required(),
  online_payment_method: yup.string().nullable(),
  online_fee_label: yup.string().nullable(),
  instore_fee_label: yup.string().nullable(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayPaymentMethodSelectedSchema = baseEventSchema.shape({
  payment_method: yup.string().required(),
  pbb_offered: yup.boolean().required(),
  is_pbb: yup.boolean().required(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayPlaidOnSuccessSchema = baseEventSchema.shape({
  institution_id: yup.string().nullable(),
  institution_name: yup.string().nullable(),
  connection_method: yup.string().required(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayPlaidOnExitSchema = baseEventSchema.shape({
  exit_point: yup.string().nullable().optional(),
  error_code: yup.string().nullable().optional(),
  error_message: yup.string().nullable().optional(),
  institution_id: yup.string().nullable().optional(),
  institution_name: yup.string().nullable().optional(),
  connection_method: yup.string().required(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayAeroSyncOnEventSchema = baseEventSchema.shape({
  event_name: yup.string().required(),
  widget_title: yup.string().required(),
  ...baseDeviceTrackingFields,
});

const dutchiePayAeroSyncOnLoadSchema = baseEventSchema.shape({
  ...baseDeviceTrackingFields,
});

const dutchiePayAeroSyncOnCloseSchema = baseEventSchema.shape({
  ...baseDeviceTrackingFields,
});

const dutchiePayAeroSyncOnSuccessSchema = baseEventSchema.shape({
  connection_id: yup.string().required(),
  client_name: yup.string().required(),
  aero_pass_user_uuid: yup.string().required(),
  ...baseDeviceTrackingFields,
});

const dutchiePayAeroSyncOnErrorSchema = baseEventSchema.shape({
  error: yup.string().required(),
  ...baseDeviceTrackingFields,
});

const dutchiePayPreAuthEventSchema = baseEventSchema.shape({
  event_name: yup.string().required(),
  error_type: yup.string().nullable(),
  error_message: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const baseInstoreCartSchema = baseEventSchema.shape({
  instore_cart_id: yup.string().required(),
  checkout_token: yup.string().nullable(),
  is_kiosk_order: yup.boolean().required(),
  is_hub_order: yup.boolean().required(),
  checkout_flow: yup.string().oneOf(Object.values(CheckoutFlow)).optional(),
});

const dutchiePayInstoreAccountLinkViewedSchema = baseEventSchema.shape({
  show_barcode: yup.boolean().required(),
});

const dutchiePayKioskQRCodeModalViewedSchema = baseEventSchema.shape({
  checkout_token: yup.string().required(),
});

const viewedKioskCheckoutModalSchema = baseEventSchema.shape({
  items: yup.array().of(cartItemSchema).min(1).required(),
  subtotal: yup.number().nullable().optional(),
});

const loginNudgeSchema = baseEventSchema.shape({
  action: yup.string().oneOf(['clicked', 'dismissed', 'viewed']).required(),
  variant: yup.string().nullable(),
  dutchie_pay_session_token: yup.string().nullable(),
});

const loginGateShownSchema = baseEventSchema.shape({
  source: yup.string().required(),
  gate_variant: yup.string().nullable(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const loginEntryPointClickedSchema = baseEventSchema.shape({
  source: yup.string().required(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const dutchiePayCheckoutFlowActionSchema = baseEventSchema.shape({
  action: yup.string().required(),
  value: yup.string().nullable(),
  dutchie_pay_session_token: yup.string().nullable(),
  ...baseDeviceTrackingFields,
});

const roundupForCharityViewedSchema = baseEventSchema;
const roundupForCharityToggledSchema = baseEventSchema.shape({
  action: yup.string().oneOf(['enabled', 'disabled']).required(),
});

const walletPassSchema = baseEventSchema.shape({
  action: yup.string().oneOf(['clicked', 'dismissed', 'viewed']).required(),
  type: yup.string().oneOf(['qrCode', 'button']).required(),
  surface: yup
    .string()
    .oneOf([...WALLET_PASS_SURFACES])
    .optional(),
});

const dutchiePayLinkToPayInitiatedSchema = baseEventSchema.shape({
  source: yup.string().oneOf(['email_cta', 'sms_cta']).required(),
  order_id: yup.string().required(),
  message_stage: yup.string().optional(),
  entry_point: yup.string().oneOf(['direct', 'post_signup']).required(),
});

const dutchiePayLinkToPayIneligibleSchema = baseEventSchema.shape({
  source: yup.string().oneOf(['email_cta', 'sms_cta']).required(),
  order_id: yup.string().required(),
  reason: yup.string().required(),
  message_stage: yup.string().optional(),
});

// PBB payment-switch CTA schemas
const dutchiePayEcommModalPaymentSwitchViewedSchema = baseEventSchema.shape({
  order_id: yup.string().required(),
  cta_variant: yup.string().oneOf(['new', 'enrolled', 'relink']).required(),
  surface: yup.string().required(),
  customer_state: yup.string().oneOf(['new', 'returning']).optional(),
  checkout_flow: yup.string().oneOf(Object.values(CheckoutFlow)).required(),
});

const dutchiePayEcommModalPaymentSwitchClickedSchema = dutchiePayEcommModalPaymentSwitchViewedSchema;

const dutchiePayEcommModalPaymentSwitchOutcomeSchema = dutchiePayEcommModalPaymentSwitchViewedSchema.shape({
  outcome: yup.string().oneOf(['success', 'failed']).required(),
  failure_reason: yup.string().optional(),
});

// Loyalty CTA schema
const dutchiePayLoyaltyCtaClickedSchema = baseEventSchema.shape({
  order_id: yup.string().required(),
  surface: yup.string().required(),
  customer_state: yup.string().oneOf(['new', 'returning']).optional(),
});

// Product Reviews & Ratings schemas
const productRatingViewedSchema = baseEventSchema.shape({
  product_id: yup.string().required(),
  avg_rating: yup.number().nullable().optional(),
  review_count: yup.number().required(),
});

const productReviewsViewedSchema = baseEventSchema.shape({
  product_id: yup.string().required(),
  review_count: yup.number().required(),
  avg_rating: yup.number().nullable().optional(),
  has_ai_summary: yup.boolean().required(),
});

const productReviewAiSummaryViewedSchema = baseEventSchema.shape({
  product_id: yup.string().required(),
});

const productReviewStatusCheckedSchema = baseEventSchema.shape({
  product_count: yup.number().required(),
  reviewed_count: yup.number().required(),
});

const productReviewCtaClickedSchema = baseEventSchema.shape({
  order_id: yup.string().required(),
  product_id: yup.string().required(),
  source: yup.string().oneOf(['order_history']).required(),
});

const productReviewSubmittedSchema = baseEventSchema.shape({
  product_id: yup.string().required(),
  rating: yup.number().required(),
  effects_count: yup.number().required(),
  has_body: yup.boolean().required(),
  source: yup.string().oneOf(['email', 'order_history']).optional(),
});

// Instore Enrollment schemas
const instoreEnrollmentStepSchema = baseEventSchema.shape({
  step: yup.string().required(),
});

const instoreEnrollmentStepCompletedSchema = instoreEnrollmentStepSchema;

const medCardSignupCompletedSchema = baseEventSchema.shape({
  waiting_at_dispensary: yup.boolean().required(),
});

const medCardSignupFailedSchema = baseEventSchema.shape({
  error_type: yup.string().required(),
  error_message: yup.string().required(),
});

const accountSignupActionSchema = baseEventSchema.shape({
  action: yup
    .string()
    .oneOf([...ACCOUNT_SIGNUP_ACTIONS])
    .required(),
  method: yup
    .string()
    .oneOf([...ACCOUNT_SIGNUP_METHODS])
    .required(),
  surface: yup
    .string()
    .oneOf([...ACCOUNT_SIGNUP_SURFACES])
    .required(),
  dutchie_pay_session_token: yup.string().optional(),
  error_code: yup.string().optional(),
  error_message: yup.string().optional(),
});

const loyaltyEnrollmentActionSchema = baseEventSchema.shape({
  surface: yup
    .string()
    .oneOf([...LOYALTY_ENROLLMENT_SURFACES])
    .required(),
  integration: yup
    .string()
    .oneOf([...LOYALTY_ENROLLMENT_INTEGRATIONS])
    .required(),
  action: yup
    .string()
    .oneOf([...LOYALTY_ENROLLMENT_ACTIONS])
    .required(),
  error_code: yup.string().optional(),
  error_message: yup.string().optional(),
});

const phoneVerificationNudgeActionSchema = baseEventSchema.shape({
  action: yup
    .string()
    .oneOf([...PHONE_VERIFICATION_NUDGE_ACTIONS])
    .required(),
  surface: yup
    .string()
    .oneOf([...PHONE_VERIFICATION_NUDGE_SURFACES])
    .required(),
  leg: yup
    .string()
    .oneOf([...PHONE_VERIFICATION_NUDGE_LEGS])
    .optional(),
  error_code: yup.string().optional(),
});

// Post-signup onboarding modal schema
const onboardingModalStepSchema = baseEventSchema.shape({
  modal: yup
    .string()
    .oneOf([...ONBOARDING_MODAL_NAMES])
    .required(),
  action: yup
    .string()
    .oneOf([...ONBOARDING_MODAL_ACTIONS])
    .required(),
  source: yup
    .string()
    .oneOf([...ONBOARDING_MODAL_SOURCES])
    .optional(),
  dutchie_pay_session_token: yup.string().optional(),
  flow_position: yup.number().optional(),
  error_code: yup.string().optional(),
  error_message: yup.string().optional(),
});

// Post-signup onboarding flow schema
const onboardingFlowStartedSchema = baseEventSchema.shape({
  source: yup
    .string()
    .oneOf([...ONBOARDING_MODAL_SOURCES])
    .required(),
  dutchie_pay_session_token: yup.string().optional(),
  // .defined() not .required(): yup's array().required() rejects [], which is a valid state here.
  eligible_steps: yup
    .array()
    .of(yup.string().oneOf([...ONBOARDING_MODAL_NAMES]))
    .defined(),
  conditional_steps: yup
    .array()
    .of(yup.string().oneOf([...ONBOARDING_MODAL_NAMES]))
    .optional(),
});

const storeRoutingFailureReasonSchema = yup.string().oneOf([...STORE_ROUTING_FAILURE_REASONS]);

const storeRoutingSchema = baseEventSchema.shape({
  action: yup
    .string()
    .oneOf([...STORE_ROUTING_ACTIONS])
    .required(),
  source: yup
    .string()
    .oneOf([...STORE_ROUTING_SOURCES])
    .required(),
  origin_dispensary_id: yup.string().required(),
  destination_dispensary_id: yup.string().optional(),
  cart_had_items: yup.boolean().optional(),
  failure_reason: storeRoutingFailureReasonSchema.when('action', {
    is: 'failed',
    then: (schema) => schema.required(),
    otherwise: (schema) =>
      schema.test('excluded', 'failure_reason is only valid for failed actions', (value) => value === undefined),
  }),
});

// Concierge schemas — data values are the registered Snowflake vocabulary (snake_case)
const conciergeComponentValues = [
  'suggested_prompts',
  'specials',
  'chips_panel',
  'product_list',
  'bundle',
  'loyalty',
  'vibe_step',
  'trending',
  'start_over',
];

// Find My Vibe step identity — required for kiosk drop-off analysis.
const conciergeVibeStepValues = ['effect', 'category', 'subcategory', 'strain'];

// Shared by conciergeImpressionSchema and conciergeActionSchema: `step` is required only when
// `component` is 'vibe_step' (mirrors the ConciergeComponentField conditional type in contract/actions.ts).
const conciergeStepSchema = yup
  .string()
  .oneOf(conciergeVibeStepValues)
  .when('component', {
    is: 'vibe_step',
    then: yup.string().oneOf(conciergeVibeStepValues).required(),
    otherwise: yup.string().oneOf(conciergeVibeStepValues).notRequired(),
  });

// conciergeExited fires from header, cart sidebar, or an in-chat checkout prompt — never chat_input.
const conciergeLocationValues = ['header', 'in_chat', 'cart_sidebar'];

const conciergePlacementValues = ['landing', 'in_chat'];

const conciergeEntryPointClickedSchema = baseEventSchema.shape({
  location: yup
    .string()
    .oneOf([
      'nav',
      'floating_button',
      'embedded_banner',
      'kiosk_pro_nav',
      'kiosk_shell',
      'product_list',
      'recommendations_home',
      'pdp_find_similar',
      'pdp_teach_me',
    ])
    .required(),
  seeded: yup.boolean().optional(),
});

const conciergeImpressionSchema = baseEventSchema.shape({
  component: yup.string().oneOf(conciergeComponentValues).required(),
  placement: yup.string().oneOf(conciergePlacementValues).required(),
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
  count: yup.number().optional(),
  step: conciergeStepSchema,
});

const conciergeActionSchema = baseEventSchema.shape({
  component: yup.string().oneOf(conciergeComponentValues).required(),
  placement: yup.string().oneOf(conciergePlacementValues).required(),
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
  label: yup.string().nullable(),
  position: yup.number().optional(),
  step: conciergeStepSchema,
});

const conciergeMessageSentSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
});

const conciergeExitedSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
  destination: yup.string().oneOf(['storefront', 'checkout']).required(),
  location: yup.string().oneOf(conciergeLocationValues).required(),
});

const conciergeBundleAddedSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
  product_count: yup.number().required(),
  total_price: yup.number().required(),
});

const conciergeAccessGateSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
  action: yup.string().oneOf(['viewed', 'login_clicked']).required(),
  reason: yup.string().oneOf(['signin_required', 'message_limit', 'kiosk_session_limit']).required(),
});

const conciergeConversationRefreshedSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
});

const conciergeMessageStoppedSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
});

const conciergeErrorSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
  kind: yup.string().oneOf(['turn_failed', 'stream_dropped']).required(),
});

const conciergeIdleWarningSchema = baseEventSchema.shape({
  action: yup.string().oneOf(['shown', 'continued', 'expired']).required(),
  conversation_id: yup.string().optional(),
});

// Kiosk usage limit reached (automatic kiosk reset). `handoff` = the counted
// handoff number that triggered the reset.
const conciergeSessionLimitReachedSchema = baseEventSchema.shape({
  conversation_id: yup.string().required(),
  turn: yup.number().required(),
  handoff: yup.number().required(),
});

export const eventsSchema = {
  addedProductToCart: addedProductToCartSchema,
  cartItem: cartItemSchema,
  placedOrder: placedOrderSchema,
  searchResults: searchResultsSchema,
  productClicked: productClickedSchema,
  sponsoredBannerImpression: bannerTrackerSchema,
  sponsoredBannerClicked: bannerTrackerSchema,
  sponsoredProductClicked: productTrackerSchema,
  sponsoredProductImpression: productTrackerSchema,
  viewedPage: viewedPageSchema,
  cartViewed: cartViewedSchema,
  storeRouting: storeRoutingSchema,
  viewedProduct: viewedProductSchema,
  removedProductFromCart: removedProductFromCartSchema,
  dispensaryClicked: dispensaryClickedSchema,
  dutchiePayEnrollmentStep: dutchiePayEnrollmentStepSchema,
  dutchiePayEnrollmentButtonClicked: dutchiePayEnrollmentButtonClickedSchema,
  dutchiePayEnrollmentStepCancelled: dutchiePayEnrollmentStepCancelledSchema,
  dutchiePayPlaidOnLoad: dutchiePayPlaidOnLoadSchema,
  dutchiePayPaymentMethodSelected: dutchiePayPaymentMethodSelectedSchema,
  dutchiePayPlaidOnEvent: dutchiePayPlaidOnEventSchema,
  dutchiePayIncentiveExposure: dutchiePayIncentiveExposureSchema,
  dutchiePayServiceFeeShown: dutchiePayServiceFeeShownSchema,
  dutchiePayPlaidOnSuccess: dutchiePayPlaidOnSuccessSchema,
  dutchiePayPlaidOnExit: dutchiePayPlaidOnExitSchema,
  dutchiePayCTAButtonClicked: dutchiePayCTAButtonClickedSchema,
  dutchiePayRelinkButtonViewed: dutchiePayRelinkButtonViewedSchema,
  dutchiePayRelinkButtonClicked: dutchiePayRelinkButtonClickedSchema,
  dutchiePayUnlinkRemoveCtaClicked: dutchiePayUnlinkRemoveCtaClickedSchema,
  dutchiePayUnlinkModalViewed: dutchiePayUnlinkModalViewedSchema,
  dutchiePayUnlinkConfirmed: dutchiePayUnlinkConfirmedSchema,
  dutchiePayUnlinkCancelled: dutchiePayUnlinkCancelledSchema,
  dutchiePayUnlinkSuccess: dutchiePayUnlinkSuccessSchema,
  dutchiePayUnlinkError: dutchiePayUnlinkErrorSchema,
  dutchiePayAeroSyncOnEvent: dutchiePayAeroSyncOnEventSchema,
  dutchiePayAeroSyncOnLoad: dutchiePayAeroSyncOnLoadSchema,
  dutchiePayAeroSyncOnClose: dutchiePayAeroSyncOnCloseSchema,
  dutchiePayAeroSyncOnSuccess: dutchiePayAeroSyncOnSuccessSchema,
  dutchiePayAeroSyncOnError: dutchiePayAeroSyncOnErrorSchema,
  dutchiePayPreAuthEvent: dutchiePayPreAuthEventSchema,
  dutchiePayInstoreAccountLinkViewed: dutchiePayInstoreAccountLinkViewedSchema,
  dutchiePayInstoreCartViewed: baseInstoreCartSchema,
  dutchiePayInstoreCartApprovalClicked: baseInstoreCartSchema,
  dutchiePayInstoreCartApprovalSucceeded: baseInstoreCartSchema,
  dutchiePayInstoreCartApprovalFailed: baseInstoreCartSchema,
  dutchiePayKioskQRCodeModalViewed: dutchiePayKioskQRCodeModalViewedSchema,
  viewedKioskCheckoutModal: viewedKioskCheckoutModalSchema,
  loginNudge: loginNudgeSchema,
  loginGateShown: loginGateShownSchema,
  loginEntryPointClicked: loginEntryPointClickedSchema,
  dutchiePayCheckoutFlowAction: dutchiePayCheckoutFlowActionSchema,
  roundupForCharityViewed: roundupForCharityViewedSchema,
  roundupForCharityToggled: roundupForCharityToggledSchema,
  walletPass: walletPassSchema,
  dutchiePayLinkToPayInitiated: dutchiePayLinkToPayInitiatedSchema,
  dutchiePayLinkToPayIneligible: dutchiePayLinkToPayIneligibleSchema,
  dutchiePayEcommModalPaymentSwitchViewed: dutchiePayEcommModalPaymentSwitchViewedSchema,
  dutchiePayEcommModalPaymentSwitchClicked: dutchiePayEcommModalPaymentSwitchClickedSchema,
  dutchiePayEcommModalPaymentSwitchOutcome: dutchiePayEcommModalPaymentSwitchOutcomeSchema,
  dutchiePayLoyaltyCtaClicked: dutchiePayLoyaltyCtaClickedSchema,
  productRatingViewed: productRatingViewedSchema,
  productReviewsViewed: productReviewsViewedSchema,
  productReviewAiSummaryViewed: productReviewAiSummaryViewedSchema,
  productReviewStatusChecked: productReviewStatusCheckedSchema,
  productReviewCtaClicked: productReviewCtaClickedSchema,
  productReviewSubmitted: productReviewSubmittedSchema,
  instoreEnrollmentStepViewed: instoreEnrollmentStepSchema,
  instoreEnrollmentStepCompleted: instoreEnrollmentStepCompletedSchema,
  instoreEnrollmentStepSkipped: instoreEnrollmentStepSchema,
  medCardSignupCompleted: medCardSignupCompletedSchema,
  medCardSignupFailed: medCardSignupFailedSchema,
  accountSignupAction: accountSignupActionSchema,
  loyaltyEnrollmentAction: loyaltyEnrollmentActionSchema,
  phoneVerificationNudgeAction: phoneVerificationNudgeActionSchema,
  onboardingModalStep: onboardingModalStepSchema,
  onboardingFlowStarted: onboardingFlowStartedSchema,
  conciergeEntryPointClicked: conciergeEntryPointClickedSchema,
  conciergeImpression: conciergeImpressionSchema,
  conciergeAction: conciergeActionSchema,
  conciergeMessageSent: conciergeMessageSentSchema,
  conciergeExited: conciergeExitedSchema,
  conciergeBundleAdded: conciergeBundleAddedSchema,
  conciergeAccessGate: conciergeAccessGateSchema,
  conciergeConversationRefreshed: conciergeConversationRefreshedSchema,
  conciergeMessageStopped: conciergeMessageStoppedSchema,
  conciergeError: conciergeErrorSchema,
  conciergeIdleWarning: conciergeIdleWarningSchema,
  conciergeSessionLimitReached: conciergeSessionLimitReachedSchema,
};

export type Data = {
  type: keyof typeof eventsSchema;
  payload: Record<string, unknown>;
};

export function validateSchema(data: Data): void {
  eventsSchema[data.type].validateSync(data.payload, { abortEarly: false });
}
